import { getToken } from "firebase/messaging";
import { messaging, onMessageListener } from "../firebase";
import axiosInstance from "../config/axiosInstance";
import { USER_API } from "../Utils/constants/apis.urls";

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

const getPlatform = () => {
  return "web";
};

export const registerDeviceToken = async (token) => {
  try {
    const platform = getPlatform();
    const response = await axiosInstance.post(USER_API.NOTIFICATIONS.DEVICES, {
      token: token,
      platform: platform,
    });

    return response.data;
  } catch (error) {

    throw error;
  }
};

export const deactivateDeviceToken = async () => {
  try {
    const response = await axiosInstance.post(
      `${USER_API.NOTIFICATIONS.DEVICES}deactivate/`,
      { platform: "web" }
    );
    return response.data;
  } catch (error) {
    console.warn("Failed to deactivate device token (non-critical):", error);
    // Don't throw — logout should still proceed even if deactivation fails
  }
};

export const requestNotificationPermission = async () => {


  try {
    // Check if browser supports notifications AND service workers
    if (!("Notification" in window)) {

      return;
    }

    if (!("serviceWorker" in navigator)) {

      return;
    }

    const permission = await Notification.requestPermission();


    if (permission === "granted") {
      // ✅ REGISTER SERVICE WORKER MANUALLY
      const swUrl = `/firebase-messaging-sw.js?apiKey=${encodeURIComponent(
        process.env.REACT_APP_FIREBASE_API_KEY
      )}&authDomain=${encodeURIComponent(
        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN
      )}&projectId=${encodeURIComponent(
        process.env.REACT_APP_FIREBASE_PROJECT_ID
      )}&messagingSenderId=${encodeURIComponent(
        process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID
      )}&appId=${encodeURIComponent(process.env.REACT_APP_FIREBASE_APP_ID)}`;

      await navigator.serviceWorker.register(swUrl);
      const registration = await navigator.serviceWorker.ready;


      // Check for pushManager explicitly
      if (!registration.pushManager) {

        return;
      }

      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {

        await registerDeviceToken(token);
      } else {

      }
    } else {

    }
  } catch (error) {

  }
};

export const playNotificationSound = () => {
  try {
    const audio = new Audio("/notification.mp3");
    audio.play().catch((err) => {
      console.warn("Autoplay blocked or MP3 playback failed:", err);
      // Fallback to WAV if MP3 fails
      const wavAudio = new Audio("/notification.wav");
      wavAudio.play().catch((e) => {
        console.warn("WAV playback failed too:", e);
      });
    });
  } catch (error) {
    console.error("Error playing notification sound:", error);
  }
};

export const setupNotificationListeners = () => {
  // 1. Listen for foreground push notifications
  const unsubscribe = onMessageListener((payload) => {
    console.log("📩 Foreground notification payload:", payload);
    const title = payload.notification?.title || payload.data?.title || "New Notification";
    const body = payload.notification?.body || payload.data?.body || "";

    // Play the sound
    playNotificationSound();

    // Dispatch global event so NotificationPopover can instantly update badge & list
    window.dispatchEvent(new CustomEvent("notification-received", { detail: payload }));

    // Show Chrome UI notification (native) instead of custom Toast
    if (Notification.permission === "granted") {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.ready
          .then((registration) => {
            registration.showNotification(title, {
              body: body,
              data: {
                click_url: payload.data?.click_url || "/",
              },
            });
          })
          .catch((err) => {
            console.warn("SW showNotification failed, using fallback:", err);
            new Notification(title, { body: body });
          });
      } else {
        new Notification(title, { body: body });
      }
    }
  });

  // 2. Listen for background messages forwarded by the Service Worker to play sound
  const handleServiceWorkerMessage = (event) => {
    if (
      event.data &&
      (event.data.type === "PLAY_NOTIFICATION_SOUND" ||
        event.data.type === "NOTIFICATION_RECEIVED")
    ) {
      console.log("🔊 Received push message from SW");
      playNotificationSound();
    }
  };

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
  }

  return () => {
    if (typeof unsubscribe === "function") unsubscribe();
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
    }
  };
};

export const getMyNotifications = async (page = 1, pageSize = 20) => {
  try {
    const response = await axiosInstance.get(USER_API.NOTIFICATIONS.MY_NOTIFICATIONS, {
      params: { page, page_size: pageSize },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching my notifications:", error);
    throw error;
  }
};

export const markNotificationRead = async (id) => {
  try {
    const response = await axiosInstance.post(
      `${USER_API.NOTIFICATIONS.MY_NOTIFICATIONS}${id}/read/`
    );
    return response.data;
  } catch (error) {
    console.error(`Error marking notification ${id} as read:`, error);
    throw error;
  }
};

export const markNotificationUnread = async (id) => {
  try {
    const response = await axiosInstance.post(
      `${USER_API.NOTIFICATIONS.MY_NOTIFICATIONS}${id}/unread/`
    );
    return response.data;
  } catch (error) {
    console.error(`Error marking notification ${id} as unread:`, error);
    throw error;
  }
};

export const markAllNotificationsRead = async () => {
  try {
    const response = await axiosInstance.post(
      `${USER_API.NOTIFICATIONS.MY_NOTIFICATIONS}read-all/`
    );
    return response.data;
  } catch (error) {
    console.error("Error marking all notifications as read:", error);
    throw error;
  }
};

export const getUnreadNotificationCount = async () => {
  try {
    const response = await axiosInstance.get(
      `${USER_API.NOTIFICATIONS.MY_NOTIFICATIONS}unread-count/`
    );
    return response.data?.unread_count || 0;
  } catch (error) {
    console.warn("Failed to fetch unread notification count:", error);
    return 0;
  }
};